import { httpClient } from '@shared/api';
import type {
  AccountBillingSummary,
  AssistantBillingSummary,
  BillingOverviewResponse,
  PaymentIntentStatusResponse,
  TopupCreateRequest,
  TopupCreateResponse,
  TransactionFilters,
  UserTransactionsResponse,
} from '../model/types';

/**
 * Биллинг-эндпоинты бэка (B-MVP-1).
 * Соответствует richat_backend/api/routers/billing.py.
 *
 * Auth: cookies (httpOnly) — httpClient шлёт withCredentials.
 *
 * Префикс /billing/ — без /api/, потому что nginx режет /api/ перед
 * проксированием (как и для остальных роутеров).
 */
export const billingApi = {
  /**
   * POST /billing/topup/create — создать платёж.
   *
   * Возвращает confirmation_url, на который фронт делает window.location.
   * Минимум 1000₽ (валидируется бэком, 422 если меньше).
   *
   * 404 — ассистент не найден / не принадлежит юзеру
   * 502 — ЮKасса недоступна
   */
  async createTopup(payload: TopupCreateRequest): Promise<TopupCreateResponse> {
    const response = await httpClient.post<TopupCreateResponse>('/billing/topup/create', payload);
    return response.data;
  },

  /**
   * GET /billing/payment-intents/{id} — статус платежа.
   * Используется для polling'а после возврата с ЮKассы.
   */
  async getPaymentIntentStatus(intentId: string): Promise<PaymentIntentStatusResponse> {
    const response = await httpClient.get<PaymentIntentStatusResponse>(
      `/billing/payment-intents/${intentId}`,
    );
    return response.data;
  },

  /** GET /billing/summary — баланс аккаунта + последние транзакции (V2-1). */
  async getAccountSummary(): Promise<AccountBillingSummary> {
    const response = await httpClient.get<AccountBillingSummary>('/billing/summary');
    return response.data;
  },

  /**
   * GET /billing/assistant/{id}/summary — баланс + последние 10 транзакций.
   */
  async getAssistantSummary(assistantId: string): Promise<AssistantBillingSummary> {
    const response = await httpClient.get<AssistantBillingSummary>(
      `/billing/assistant/${assistantId}/summary`,
    );
    return response.data;
  },

  /**
   * GET /billing/transactions — общая история всех ассистентов юзера.
   * Пагинация через limit/offset. has_more в ответе.
   *
   * B-MVP-4a: поддержаны фильтры assistant_id / kind / date_from / date_to.
   * Пустые/undefined поля не отправляем (axios опускает undefined в params).
   */
  async getUserTransactions(
    limit = 50,
    offset = 0,
    filters: TransactionFilters = {},
  ): Promise<UserTransactionsResponse> {
    const response = await httpClient.get<UserTransactionsResponse>('/billing/transactions', {
      params: {
        limit,
        offset,
        assistant_id: filters.assistant_id || undefined,
        kind: filters.kind || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
      },
    });
    return response.data;
  },

  /**
   * GET /billing/overview — агрегированная сводка за период (B-MVP-4a).
   *
   * Без date_from/date_to — сводка за всё время.
   * Возвращает totals (paid/welcome/chat/ingest/spent) + разбивки
   * by_channel / by_assistant / by_day.
   */
  async getOverview(
    filters: Pick<TransactionFilters, 'date_from' | 'date_to'> = {},
  ): Promise<BillingOverviewResponse> {
    const response = await httpClient.get<BillingOverviewResponse>('/billing/overview', {
      params: {
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
      },
    });
    return response.data;
  },
};
